import { useEffect, useState } from "react";
import MainCalculatorsList from "./MainCalculators";
import SharedHelmet from ".././Helmet";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";

const TITLE = "حاسبة جوجل ادسنس - حساب ارباح جوجل ادسنس";
const DESCRIPTION =
  "مع هذه الحاسبة لـ جوجل ادسنس نهدف إلى مساعدتك في حساب الإيرادات التي يمكنك الحصول عليها من برنامج جوجل ادسنس.";
const KEYWORDS =
  "حاسبة جوجل ادسنس، حساب ارباح جوجل ادسنس، إعدادات الإيرادات من جوجل ادسنس، برنامج جوجل ادسنس، حساب الإيرادات اليومية من جوجل ادسنس، الربح من الإعلانات عبر الإنترنت";
const OG_URL = "https://do-calculate.com/calculator/ar/google-adsense/";

const conversionMap = {
  day: 1,
  week: 7,
  month: 30.436875,
  year: 365.2425,
};

const inputStyle = {
  width: "200px",
  marginLeft: "10px",
  backgroundColor: "#f9f9f9",
  border: "1px solid rgba(0, 0, 0, 0.23)",
  borderRadius: "4px",
  padding: "8.5px 14px",
  fontSize: "14px",
  outlineColor: "#2e7d32",
};

const selectStyle = {
  ...inputStyle,
  width: "auto",
  padding: "7.5px 10px",
  cursor: "pointer",
};

const labelStyle = {
  display: "block",
  fontSize: "12px",
  color: "#666",
  marginBottom: "4px",
};

const GoogleAdsenseCalculator = () => {
  // ── Inputs ──────────────────────────────────────────────────────────────
  const [pageViews, setPageViews] = useState("");
  const [pageViewsPeriod, setPageViewsPeriod] = useState("day");
  const [adsPerPage, setAdsPerPage] = useState("");
  const [CTR, setCTR] = useState("");
  const [CPC, setCPC] = useState("");

  // ── Output period selectors ─────────────────────────────────────────────
  const [conversionImpressions, setConversionImpressions] = useState("day");
  const [conversionClicks, setConversionClicks] = useState("day");
  const [conversionRevenue, setConversionRevenue] = useState("day");

  // ── Computed results ────────────────────────────────────────────────────
  const [adImpressions, setAdImpressions] = useState("");
  const [numberOfClicks, setNumberOfClicks] = useState("");
  const [googleAdseneRevenue, setGoogleAdseneRevenue] = useState("");

  // ── Single unified recalculation effect ────────────────────────────────
  // All outputs are derived from a normalised "per day" base, then scaled
  // to whatever output period the user has selected. This fixes the bug in
  // the original code where output-period changes were not recalculating
  // correctly and CTR/CPC rejected decimal values.
  useEffect(() => {
    const views     = parseFloat(pageViews);
    const ads       = parseFloat(adsPerPage);
    const ctr       = parseFloat(CTR) / 100;   // % → ratio
    const cpc       = parseFloat(CPC);
    const inFactor  = conversionMap[pageViewsPeriod];

    if (isNaN(views) || views <= 0 || !inFactor) {
      setAdImpressions("");
      setNumberOfClicks("");
      setGoogleAdseneRevenue("");
      return;
    }

    const dailyViews = views / inFactor; // normalise to per-day

    if (isNaN(ads) || ads <= 0) {
      setAdImpressions("");
      setNumberOfClicks("");
      setGoogleAdseneRevenue("");
      return;
    }

    const dailyImpressions = dailyViews * ads;
    setAdImpressions(
      Math.round(dailyImpressions * conversionMap[conversionImpressions])
    );

    if (isNaN(ctr)) {
      setNumberOfClicks("");
      setGoogleAdseneRevenue("");
      return;
    }

    const dailyClicks = dailyImpressions * ctr;
    setNumberOfClicks(
      Math.round(dailyClicks * conversionMap[conversionClicks])
    );

    if (isNaN(cpc)) {
      setGoogleAdseneRevenue("");
      return;
    }

    const dailyRevenue = dailyClicks * cpc;
    setGoogleAdseneRevenue(
      (dailyRevenue * conversionMap[conversionRevenue]).toFixed(2)
    );
  }, [
    pageViews,
    pageViewsPeriod,
    adsPerPage,
    CTR,
    CPC,
    conversionImpressions,
    conversionClicks,
    conversionRevenue,
  ]);

  // ── Input handlers ──────────────────────────────────────────────────────
  const handlePageViewsChange = (e) => {
    if (/^\d*$/.test(e.target.value)) setPageViews(e.target.value);
  };

  const handleAdsPerPageChange = (e) => {
    if (/^\d*$/.test(e.target.value)) setAdsPerPage(e.target.value);
  };

  // Fixed: allow decimals (e.g. 2.5%)
  const handleCTRChange = (e) => {
    if (/^\d*\.?\d*$/.test(e.target.value)) setCTR(e.target.value);
  };

  // Fixed: allow decimals (e.g. $0.50)
  const handleCPCChange = (e) => {
    if (/^\d*\.?\d*$/.test(e.target.value)) setCPC(e.target.value);
  };

  // ── Reset ────────────────────────────────────────────────────────────────
  const handleReset = () => {
    setPageViews("");
    setPageViewsPeriod("day");
    setAdsPerPage("");
    setCTR("");
    setCPC("");
    setConversionImpressions("day");
    setConversionClicks("day");
    setConversionRevenue("day");
    setAdImpressions("");
    setNumberOfClicks("");
    setGoogleAdseneRevenue("");
  };

  const PeriodSelect = ({ value, onChange }) => (
    <select value={value} onChange={onChange} style={selectStyle}>
      <option value="day">Per day</option>
      <option value="week">Per week</option>
      <option value="month">Per month</option>
      <option value="year">Per year</option>
    </select>
  );

  return (
    <div className="rtl">
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ height: "50px", display: "flex", alignItems: "center", justifyContent: "center" }}
          >
            <h1 style={{ fontSize: "17px", margin: 0 }}>{TITLE}</h1>
          </div>

          <div className="row">
            <div>
              <h2 style={{ fontSize: "18px" }}>ماهو Google AdSense :</h2>
              <p>
                هو برنامج تم تطويره بواسطة Google يسمح لناشري مواقع الويب بتحقيق
                الدخل من محتواهم من خلال عرض إعلانات مستهدفة على مواقعهم على
                الويب. <br /> يقوم المعلنون بالمزايدة على المساحة الإعلانية،
                وتقوم Google بمطابقة الإعلانات ذات الصلة بمحتوى موقع الويب.
              </p>
              <p>
                كيف يعمل Google AdSense ؟ <br /> عندما ينقر الزائر على أحد
                الإعلانات، يحصل ناشر موقع الويب على جزء من الإيرادات التي يحققها
                المعلن. يعد برنامج AdSense أحد أشهر البرامج الإعلانية على
                الإنترنت، ويمكن أن يكون مصدرًا هامًا للدخل لأصحاب مواقع الويب
                الذين لديهم قدر كبير من الزيارات. بالإضافة إلى ذلك، فإن الانضمام
                إلى Google AdSense مجاني وسهل الإعداد والإدارة.
              </p>
              <p>
                كيف يتم حساب ايرادات Google AdSense : <br />
                حساب عدد مرات ظهور الاعلان : لحساب عدد مرات ظهور الاعلان نقوم
                بضرب عدد مشاهدات الصفحة بعدد الاعلانات في كل صفحة.
                <br />
                عدد النقرات : لحساب عدد النقرات نقوم بضرب مرات ظهور الاعلان
                بنسبة النقر إلى الظهور
                <br />
                ايرادات Google AdSense : لحساب ايرادات Google AdSense نقوم بضرب
                عدد مشاهدات الصفحة بعدد الاعلانات في كل صفحة بنسبة النقر إلى
                الظهور بتكلفة النقرة أي : <br />
                Google AdSense revenue = pageviews × ads per page × CTR × CPC
              </p>
              <p>او يتم حساب ايرادات Google AdSense عبر الحاسبة التالية :</p>
              <hr />
            </div>
          </div>

          <MobileUnderCalAds />

          <div className="row">
            <div className="col-sm-12 col-md-7 calculatorStyle">
              <h3
                className="p-3 mb-3 bg-success text-white"
                style={{ fontSize: "16px", fontWeight: "bold", borderRadius: "4px" }}
              >
                حساب ارباح جوجل ادسنس - Google Adsense Calculator
              </h3>

              {/* ── Page Views ── */}
              <div className="mb-3 d-flex" style={{ alignItems: "flex-end" }}>
                <div>
                  <label style={labelStyle}>عدد مشاهدات الصفحة</label>
                  <input
                    style={inputStyle}
                    type="text"
                    inputMode="numeric"
                    value={pageViews}
                    onChange={handlePageViewsChange}
                    placeholder="مثال: 100000"
                  />
                </div>
                <PeriodSelect
                  value={pageViewsPeriod}
                  onChange={(e) => setPageViewsPeriod(e.target.value)}
                />
              </div>

              {/* ── Ads Per Page ── */}
              <div className="mb-3 d-flex" style={{ alignItems: "flex-end" }}>
                <div>
                  <label style={labelStyle}>عدد الاعلانات في كل صفحة</label>
                  <input
                    style={inputStyle}
                    type="text"
                    inputMode="numeric"
                    value={adsPerPage}
                    onChange={handleAdsPerPageChange}
                    placeholder="مثال: 3"
                  />
                </div>
                <div style={{ padding: "8.5px 12px", fontSize: "14px", color: "#666" }}>
                  Per Page
                </div>
              </div>

              {/* ── Ad Impressions (result) ── */}
              <div className="mb-3 d-flex" style={{ alignItems: "flex-end" }}>
                <div>
                  <label style={labelStyle}>مرات ظهور الاعلان</label>
                  <input
                    style={inputStyle}
                    type="text"
                    value={adImpressions}
                    readOnly
                    placeholder="—"
                  />
                </div>
                <PeriodSelect
                  value={conversionImpressions}
                  onChange={(e) => setConversionImpressions(e.target.value)}
                />
              </div>

              <hr />

              {/* ── CTR ── */}
              <div className="mb-3 d-flex" style={{ alignItems: "flex-end" }}>
                <div>
                  <label style={labelStyle}>نسبة النقر الى الظهور</label>
                  <input
                    style={inputStyle}
                    type="text"
                    inputMode="decimal"
                    value={CTR}
                    onChange={handleCTRChange}
                    placeholder="مثال: 2.5"
                  />
                </div>
                <div style={{ fontSize: "20px", paddingBottom: "5px" }}>%</div>
              </div>

              {/* ── Clicks (result) ── */}
              <div className="mb-3 d-flex" style={{ alignItems: "flex-end" }}>
                <div>
                  <label style={labelStyle}>عدد النقرات</label>
                  <input
                    style={inputStyle}
                    type="text"
                    value={numberOfClicks}
                    readOnly
                    placeholder="—"
                  />
                </div>
                <PeriodSelect
                  value={conversionClicks}
                  onChange={(e) => setConversionClicks(e.target.value)}
                />
              </div>

              <hr />

              {/* ── CPC ── */}
              <div className="mb-3 d-flex" style={{ alignItems: "flex-end" }}>
                <div>
                  <label style={labelStyle}>تكلفة النقرة</label>
                  <input
                    style={inputStyle}
                    type="text"
                    inputMode="decimal"
                    value={CPC}
                    onChange={handleCPCChange}
                    placeholder="مثال: 0.50"
                  />
                </div>
                <div style={{ fontSize: "20px", paddingBottom: "5px" }}>$</div>
              </div>

              {/* ── Revenue (result) ── */}
              <div className="mb-3 d-flex" style={{ alignItems: "flex-end" }}>
                <div>
                  <label style={labelStyle}>ايرادات Google Adsense</label>
                  <input
                    style={inputStyle}
                    type="text"
                    value={googleAdseneRevenue !== "" ? `${googleAdseneRevenue} $` : ""}
                    readOnly
                    placeholder="—"
                  />
                </div>
                <PeriodSelect
                  value={conversionRevenue}
                  onChange={(e) => setConversionRevenue(e.target.value)}
                />
              </div>

              {/* ── Reset ── */}
              <div className="mt-4">
                <button
                  style={{
                    backgroundColor: "#6c757d",
                    color: "white",
                    border: "none",
                    padding: "10px 25px",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontSize: "15px",
                  }}
                  onClick={handleReset}
                >
                  امسح النتيجة
                </button>
              </div>
            </div>
          </div>

          <hr />
          <div className="row">
            <RelatedLinks />
            <hr />
          </div>
        </div>

        <div className="col">
          <SecondAds />
          <br />
          <MainCalculatorsList CalculatorsTypeId={500} />
          <br />
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          SEO INFORMATIONAL CONTENT
      ════════════════════════════════════════════════════════════════ */}
      <div className="row" style={{ marginTop: "20px" }}>
        <div className="col-12 col-md-8">
          <div className="info-sections-container">

            {/* ── Section 1: What is this calculator ── */}
            <div className="info-section">
              <div className="info-section-header">
                <strong>ما هي حاسبة جوجل ادسنس؟</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  <strong>حاسبة جوجل ادسنس</strong> هي أداة إلكترونية مجانية تساعدك على
                  تقدير الأرباح التي يمكنك تحقيقها من برنامج Google AdSense بناءً على
                  إحصائيات موقعك الفعلية. تتيح لك الحاسبة:
                </p>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>حساب مرات ظهور الإعلان:</strong> بضرب عدد مشاهدات الصفحة
                    في عدد الإعلانات لكل صفحة
                  </li>
                  <li>
                    <strong>تقدير عدد النقرات:</strong> باستخدام نسبة النقر إلى الظهور
                    (CTR) التي يحققها موقعك
                  </li>
                  <li>
                    <strong>حساب إيراداتك المتوقعة:</strong> بضرب عدد النقرات في
                    متوسط تكلفة النقرة (CPC)
                  </li>
                  <li>
                    <strong>تحويل الفترات الزمنية:</strong> عرض النتائج يومياً أو
                    أسبوعياً أو شهرياً أو سنوياً
                  </li>
                  <li>
                    <strong>تخطيط الدخل:</strong> وضع توقعات واقعية لدخلك من الإعلانات
                  </li>
                </ul>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                  كيفية استخدام حاسبة AdSense:
                </h3>
                <ol style={{ lineHeight: "1.8" }}>
                  <li>أدخل عدد مشاهدات الصفحة اليومية أو الشهرية لموقعك</li>
                  <li>أدخل متوسط عدد الإعلانات في كل صفحة (الحد الأقصى 3)</li>
                  <li>
                    أدخل نسبة النقر إلى الظهور (CTR) — يتراوح المتوسط بين 1% و
                    5%
                  </li>
                  <li>
                    أدخل متوسط تكلفة النقرة (CPC) بالدولار — تختلف حسب المجال
                    والبلد
                  </li>
                  <li>ستظهر النتائج تلقائياً مع إمكانية تغيير الفترة الزمنية</li>
                </ol>
                <div className="alert alert-info mt-3" style={{ lineHeight: "1.8" }}>
                  <strong>💡 ملاحظة:</strong> هذه الحاسبة تعطيك تقديراً استرشادياً.
                  الأرباح الفعلية تتأثر بعوامل عديدة مثل موضوع الموقع، ودولة
                  الزوار، وموسمية الإعلانات.
                </div>
              </div>
            </div>

            {/* ── Section 2: How AdSense works ── */}
            <div className="info-section">
              <div className="info-section-header">
                <strong>كيف يعمل Google AdSense وكيف تزيد أرباحك؟</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  يعمل Google AdSense على نموذج المزاد الفوري — في كل مرة يفتح
                  زائر صفحتك، يتنافس المعلنون على مساحة الإعلان وتُعرض الإعلان
                  الفائز تلقائياً. إليك أبرز العوامل التي تؤثر في أرباحك:
                </p>
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>
                  1. اختيار مجال ذو CPC مرتفع:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>المجالات الأعلى ربحاً:</strong> التأمين، القانون،
                    التمويل، الصحة، العقارات
                  </li>
                  <li>
                    <strong>المجالات المتوسطة:</strong> التقنية، التعليم، السفر،
                    التسوق الإلكتروني
                  </li>
                  <li>
                    <strong>المجالات المنخفضة:</strong> الترفيه، الأخبار العامة،
                    المحتوى الترفيهي
                  </li>
                </ul>
                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>
                  2. تحسين موضع الإعلانات:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>الإعلانات فوق الطية:</strong> الجزء المرئي دون تمرير
                    يحقق نسب نقر أعلى بكثير
                  </li>
                  <li>
                    <strong>داخل المحتوى:</strong> الإعلانات المدمجة في النص تحقق
                    أداءً ممتازاً
                  </li>
                  <li>
                    <strong>نهاية المقال:</strong> القراء المهتمون يُكملون المقال
                    وهم أكثر ميلاً للنقر
                  </li>
                </ul>
                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>
                  3. تحسين جودة حركة المرور:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>الزوار من الدول المتقدمة:</strong> الولايات المتحدة،
                    كندا، المملكة المتحدة تحقق CPC أعلى
                  </li>
                  <li>
                    <strong>حركة البحث العضوي:</strong> زوار قادمون من Google
                    يتفاعلون أكثر مع الإعلانات
                  </li>
                  <li>
                    <strong>المحتوى المستهدف:</strong> الزوار ذوو النية الشرائية
                    يُحقّقون إيرادات أعلى
                  </li>
                </ul>
                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>
                  4. تحسين تجربة المستخدم:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>سرعة الموقع:</strong> المواقع السريعة تُحسّن معدلات
                    الارتداد وتزيد مشاهدات الصفحات
                  </li>
                  <li>
                    <strong>التوافق مع الهاتف:</strong> أكثر من 60% من الإعلانات
                    تُعرض على الأجهزة المحمولة
                  </li>
                  <li>
                    <strong>محتوى جذّاب:</strong> كلما أمضى الزائر وقتاً أطول،
                    زادت فرص ظهور الإعلانات ونقرها
                  </li>
                </ul>
              </div>
            </div>

            {/* ── Section 3: CTR / CPC / RPM explained ── */}
            <div className="info-section">
              <div className="info-section-header">
                <strong>شرح المصطلحات: CTR و CPC و RPM و CPM</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  لتستخدم حاسبة AdSense بشكل صحيح وتفهم تقاريرك، عليك معرفة هذه
                  المصطلحات الأساسية:
                </p>
                <table className="table bmi-table" style={{ marginTop: "1rem" }}>
                  <thead>
                    <tr>
                      <th className="bmi-header" style={{ textAlign: "center" }}>المصطلح</th>
                      <th className="bmi-header" style={{ textAlign: "center" }}>المعنى</th>
                      <th className="bmi-header" style={{ textAlign: "center" }}>المتوسط الشائع</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ textAlign: "center" }}><strong>CTR</strong> — نسبة النقر إلى الظهور</td>
                      <td style={{ textAlign: "center" }}>النقرات ÷ مرات الظهور × 100</td>
                      <td style={{ textAlign: "center" }}>1% — 3%</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}><strong>CPC</strong> — تكلفة النقرة</td>
                      <td style={{ textAlign: "center" }}>ما تكسبه في كل نقرة</td>
                      <td style={{ textAlign: "center" }}>$0.10 — $2.00</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}><strong>RPM</strong> — الإيراد لكل 1000 ظهور</td>
                      <td style={{ textAlign: "center" }}>الإيرادات ÷ مرات الظهور × 1000</td>
                      <td style={{ textAlign: "center" }}>$1 — $10</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}><strong>CPM</strong> — تكلفة الألف ظهور</td>
                      <td style={{ textAlign: "center" }}>ما يدفعه المعلن لكل 1000 ظهور</td>
                      <td style={{ textAlign: "center" }}>$0.50 — $5</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}><strong>Page RPM</strong></td>
                      <td style={{ textAlign: "center" }}>الإيرادات ÷ مشاهدات الصفحة × 1000</td>
                      <td style={{ textAlign: "center" }}>$1 — $15</td>
                    </tr>
                  </tbody>
                </table>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                  ما هو متوسط CTR الجيد لـ AdSense؟
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  يتراوح متوسط CTR لمعظم المواقع بين <strong>1% و 3%</strong>. أي
                  نسبة أعلى من 3% تعتبر ممتازة، لكن انتبه: نسبة CTR مرتفعة جداً
                  (أكثر من 10%) قد تُثير مخاوف Google بشأن النقرات غير الصحيحة.
                </p>
                <div className="alert alert-success mt-3" style={{ lineHeight: "1.8" }}>
                  <strong>✓ نصيحة:</strong> يمكنك استخدام هذه الحاسبة بشكل عكسي —
                  أدخل الإيراد المستهدف وجرّب قيم CTR و CPC المختلفة لتعرف ما
                  تحتاج تحقيقه للوصول إلى هدفك.
                </div>
              </div>
            </div>

            {/* ── Section 4: Practical examples ── */}
            <div className="info-section">
              <div className="info-section-header">
                <strong>أمثلة عملية على حساب أرباح AdSense</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>
                  مثال 1: موقع مدوّنة متوسط
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>البيانات:</strong> 5,000 مشاهدة يومياً، 3 إعلانات في كل
                  صفحة، CTR = 2%، CPC = $0.30
                </p>
                <div className="alert alert-info" style={{ fontSize: "14px", lineHeight: "1.8" }}>
                  <strong>الحساب:</strong><br />
                  مرات ظهور الإعلان = 5,000 × 3 = 15,000 ظهور/يوم<br />
                  عدد النقرات = 15,000 × 2% = 300 نقرة/يوم<br />
                  الإيراد اليومي = 300 × $0.30 = <strong>$90/يوم</strong><br />
                  الإيراد الشهري ≈ <strong>$2,700/شهر</strong>
                </div>

                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>
                  مثال 2: موقع تقني متخصص
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>البيانات:</strong> 2,000 مشاهدة يومياً، 3 إعلانات، CTR =
                  1.5%، CPC = $1.50
                </p>
                <div className="alert alert-info" style={{ fontSize: "14px", lineHeight: "1.8" }}>
                  <strong>الحساب:</strong><br />
                  مرات ظهور الإعلان = 2,000 × 3 = 6,000 ظهور/يوم<br />
                  عدد النقرات = 6,000 × 1.5% = 90 نقرة/يوم<br />
                  الإيراد اليومي = 90 × $1.50 = <strong>$135/يوم</strong><br />
                  الإيراد الشهري ≈ <strong>$4,050/شهر</strong>
                </div>
                <p style={{ lineHeight: "1.8" }}>
                  لاحظ كيف أن الموقع التقني يحقق إيراداً أعلى رغم عدد الزيارات
                  الأقل — بسبب ارتفاع قيمة CPC في مجال التقنية.
                </p>

                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>
                  مثال 3: موقع إخباري كبير
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>البيانات:</strong> 100,000 مشاهدة يومياً، 3 إعلانات، CTR
                  = 1%، CPC = $0.15
                </p>
                <div className="alert alert-info" style={{ fontSize: "14px", lineHeight: "1.8" }}>
                  <strong>الحساب:</strong><br />
                  مرات ظهور الإعلان = 100,000 × 3 = 300,000 ظهور/يوم<br />
                  عدد النقرات = 300,000 × 1% = 3,000 نقرة/يوم<br />
                  الإيراد اليومي = 3,000 × $0.15 = <strong>$450/يوم</strong><br />
                  الإيراد الشهري ≈ <strong>$13,500/شهر</strong>
                </div>
              </div>
            </div>

            {/* ── Section 5: FAQ ── */}
            <div className="info-section">
              <div className="info-section-header">
                <strong>أسئلة شائعة حول Google AdSense</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "8px" }}>
                  كم يدفع AdSense لكل 1000 مشاهدة؟
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  يتراوح متوسط الأرباح لكل 1000 مشاهدة (Page RPM) بين{" "}
                  <strong>$1 و $10</strong> في معظم المجالات، وقد يصل إلى $50 أو
                  أكثر في مجالات مثل التأمين والقانون. الأرقام تختلف بشكل كبير
                  حسب بلد الزوار ومجال الموقع وجودة المحتوى.
                </p>

                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "8px" }}>
                  ما الحد الأدنى لعدد الزيارات للقبول في AdSense؟
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  لا يوجد حد أدنى رسمي لعدد الزيارات، لكن يُنصح بامتلاك موقع به{" "}
                  <strong>محتوى أصلي وكافٍ</strong> (لا يقل عن 20-30 مقالاً) قبل
                  التقديم. التركيز يجب أن يكون على جودة المحتوى والالتزام بسياسات
                  Google.
                </p>

                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "8px" }}>
                  هل يمكن استخدام AdSense مع مواقع اللغة العربية؟
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  نعم، <strong>Google AdSense يدعم المواقع العربية بشكل كامل</strong>.
                  تُعدّ المملكة العربية السعودية والإمارات ومصر من أكثر الأسواق العربية
                  إنفاقاً على الإعلانات، مما يعني CPC معقول يتراوح بين $0.05 و $0.50
                  في معظم المجالات.
                </p>

                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "8px" }}>
                  متى تصل أرباح AdSense إلى حساب البنك؟
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  يصدر Google AdSense المدفوعات شهرياً عند تجاوز الرصيد حد{" "}
                  <strong>$100</strong>. يتم تأكيد الأرباح في نهاية كل شهر، وتُحوَّل
                  في الفترة بين 21 و 26 من الشهر التالي.
                </p>

                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "8px" }}>
                  ما الفرق بين AdSense و AdMob؟
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>Google AdSense</strong> مخصص للمواقع الإلكترونية على سطح
                  المكتب والهاتف المحمول، بينما{" "}
                  <strong>Google AdMob</strong> مخصص لتطبيقات الهاتف المحمول (iOS
                  و Android). كلاهما من Google ويستخدمان نفس شبكة المعلنين.
                </p>

                <div className="alert alert-success mt-3" style={{ lineHeight: "1.8" }}>
                  <strong>✓ خلاصة:</strong> استخدم حاسبة AdSense أعلاه لوضع
                  توقعات واقعية، ثم ركّز على زيادة حركة المرور وتحسين جودة
                  المحتوى لتحقيق أعلى أرباح ممكنة من برنامج Google AdSense.
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default GoogleAdsenseCalculator;